import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDateTime, KpiCard, percent, StatusPill } from './DetailDashboard';
import Pagination from './Pagination';
import SearchableSelect from './SearchableSelect';
import DateRangeFilter, { toInputDate } from './DateRangeFilter';
import { downloadCsv } from '../utils/csv';
import styles from '../pages/assets/css/DetailDashboard.module.css';
import usageStyles from './EquipmentUsageView.module.css';

const PAGE_SIZE = 25;

// tone is a StatusPill value that maps to the wanted colour.
const ISSUES = {
  not_pulling: { label: 'Not pulling worklist', tone: 'failed' },
  not_sending_results: { label: 'Pulling, not sending results', tone: 'failed' },
  misconfigured: { label: 'Equipment / modality misconfigured', tone: 'pending' },
  not_linked: { label: 'Not linked', tone: 'cancelled' },
  no_activity: { label: 'No activity', tone: 'cancelled' },
  ok: { label: 'OK', tone: 'completed' },
};

const COMPLETION = {
  completed: { label: 'All completed', tone: 'completed' },
  partial: { label: 'Partly completed', tone: 'in_progress' },
  none: { label: 'None completed', tone: 'failed' },
};

const LIFECYCLE_STATES = {
  active: 'Active',
  inactive: 'Inactive',
  maintenance: 'Maintenance',
  disabled: 'Disabled',
  retired: 'Retired',
};

const OPERATIONAL_STATUSES = {
  operational: 'Operational',
  non_operational: 'Non-operational',
  maintenance: 'Maintenance',
  unknown: 'Unknown',
};

const issueRank = Object.keys(ISSUES);

const COLUMNS = [
  { key: 'facility_name', label: 'Facility' },
  { key: 'modality', label: 'Modality' },
  { key: 'equipment_name', label: 'Equipment' },
  { key: 'vendor_name', label: 'Vendor' },
  { key: 'worklists', label: 'Worklists', numeric: true },
  { key: 'pulled', label: 'Pulled', numeric: true },
  { key: 'completed', label: 'Completed', numeric: true },
  { key: 'studies', label: 'Studies', numeric: true },
  { key: 'last_pulled_at', label: 'Last pull', numeric: true },
  { key: 'last_completed_at', label: 'Last result', numeric: true },
  { key: 'issue', label: 'Issue', value: (row) => issueRank.indexOf(row.issue) },
];

// Counties are compared ignoring case and spacing, as the API filters them.
const sameCounty = (a, b) => (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();

const defaultFilters = () => {
  const today = new Date();
  const start = new Date(today);
  start.setDate(start.getDate() - 29);
  return {
    start_date: toInputDate(start),
    end_date: toInputDate(today),
    vendor_id: '',
    county: '',
    facility_id: '',
    modality: '',
    hmis: '',
    lifecycle_state: 'active',
    operational_status: '',
    completion_status: '',
    issue: '',
  };
};

const compare = (a, b) => {
  if (a == null || a === '') return b == null || b === '' ? 0 : 1; // blanks last
  if (b == null || b === '') return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

/**
 * Filters, totals and a sortable per-equipment table of worklists published, pulled and completed.
 * `fetchUsage(params)` loads the data; `linkEquipment` links rows to the staff equipment page.
 */
const EquipmentUsageView = ({ fetchUsage, linkEquipment = false, onError }) => {
  const [filters, setFilters] = useState(defaultFilters);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sort, setSort] = useState({ key: 'issue', direction: 'asc' });
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, value]) => value));
      setData(await fetchUsage(params));
    } catch (err) {
      setError(err?.detail || 'Failed to load equipment usage');
      if (onError) onError(err);
    } finally {
      setLoading(false);
    }
  }, [fetchUsage, filters, onError]);

  useEffect(() => {
    load();
  }, [load]);

  const setFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  };

  const toggleSort = (column) => {
    setSort((current) => (
      current.key === column.key
        ? { key: column.key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key: column.key, direction: column.numeric ? 'desc' : 'asc' }
    ));
    setPage(1);
  };

  const sortedItems = useMemo(() => {
    const column = COLUMNS.find((item) => item.key === sort.key);
    const valueOf = column?.value || ((row) => row[sort.key]);
    const sign = sort.direction === 'asc' ? 1 : -1;
    return [...(data?.items || [])].sort((a, b) => {
      const va = valueOf(a);
      const vb = valueOf(b);
      const blank = (value) => value == null || value === '';
      // Blanks stay last in either direction.
      if (blank(va) || blank(vb)) return compare(va, vb);
      return sign * compare(va, vb) || compare(a.facility_name, b.facility_name) || compare(a.modality, b.modality);
    });
  }, [data, sort]);

  const pageItems = sortedItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const summary = data?.summary;
  const options = data?.options || { vendors: [], facilities: [], modalities: [], hmis: [], counties: [] };
  const facilityOptions = filters.county
    ? options.facilities.filter((facility) => sameCounty(facility.county, filters.county))
    : options.facilities;
  // A facility outside the chosen county would leave nothing to show, so it is cleared.
  const setCounty = (county) => {
    setFilters((current) => {
      const facility = options.facilities.find((item) => item.id === current.facility_id);
      const keepFacility = !county || !facility || sameCounty(facility.county, county);
      return { ...current, county, facility_id: keepFacility ? current.facility_id : '' };
    });
    setPage(1);
  };

  const exportCsv = () => downloadCsv(
    `equipment-usage_${filters.start_date || 'all'}_${filters.end_date || 'all'}.csv`,
    sortedItems,
    [
      ['facility_name', 'Facility'],
      ['county', 'County'],
      ['hmis', 'HMIS'],
      ['modality', 'Modality'],
      ['equipment_name', 'Equipment'],
      ['asset_id', 'Asset ID'],
      ['ae_title', 'AE Title'],
      ['vendor_name', 'Vendor'],
      [(row) => LIFECYCLE_STATES[row.lifecycle_state] || row.lifecycle_state, 'Status'],
      [(row) => OPERATIONAL_STATUSES[row.operational_status] || row.operational_status, 'Operational status'],
      ['worklists', 'Worklists'],
      ['pulled', 'Pulled'],
      ['completed', 'Completed'],
      ['queries', 'Worklist queries'],
      ['studies', 'Studies received'],
      ['unmatched_studies', 'Unmatched studies'],
      ['last_pulled_at', 'Last pull'],
      ['last_completed_at', 'Last result'],
      [(row) => COMPLETION[row.completion_status]?.label || '', 'Completion'],
      [(row) => ISSUES[row.issue]?.label || row.issue, 'Issue'],
      ['issue_detail', 'Issue detail'],
    ],
  );

  return (
    <>
      <section className={styles.card}>
        <div className={`${styles.filterRow} ${usageStyles.filterRow}`}>
          <DateRangeFilter
            start={filters.start_date}
            end={filters.end_date}
            onChange={({ start, end }) => {
              setFilters((current) => ({ ...current, start_date: start, end_date: end }));
              setPage(1);
            }}
          />
          <label className={usageStyles.field}>
            <span>Vendor</span>
            <select className={styles.filterInput} value={filters.vendor_id} onChange={(e) => setFilter('vendor_id', e.target.value)}>
              <option value="">All vendors</option>
              {options.vendors.map((vendor) => <option key={vendor.id} value={vendor.id}>{vendor.name}</option>)}
            </select>
          </label>
          <label className={usageStyles.field}>
            <span>County</span>
            <select className={styles.filterInput} value={filters.county} onChange={(e) => setCounty(e.target.value)}>
              <option value="">All counties</option>
              {(options.counties || []).map((county) => <option key={county} value={county}>{county}</option>)}
            </select>
          </label>
          <div className={usageStyles.field}>
            <span>Facility</span>
            <SearchableSelect
              className={`${styles.filterInput} ${usageStyles.wideSelect}`}
              options={facilityOptions}
              value={filters.facility_id}
              onChange={(value) => setFilter('facility_id', value)}
              emptyLabel="All facilities"
              ariaLabel="Facility"
            />
          </div>
          <label className={usageStyles.field}>
            <span>Modality</span>
            <select className={styles.filterInput} value={filters.modality} onChange={(e) => setFilter('modality', e.target.value)}>
              <option value="">All modalities</option>
              {options.modalities.map((modality) => <option key={modality} value={modality}>{modality}</option>)}
            </select>
          </label>
          <label className={usageStyles.field}>
            <span>HMIS</span>
            <select className={styles.filterInput} value={filters.hmis} onChange={(e) => setFilter('hmis', e.target.value)}>
              <option value="">All HMIS</option>
              {(options.hmis || []).map((hmis) => <option key={hmis} value={hmis}>{hmis}</option>)}
            </select>
          </label>
          <label className={usageStyles.field}>
            <span>Status</span>
            <select className={styles.filterInput} value={filters.lifecycle_state} onChange={(e) => setFilter('lifecycle_state', e.target.value)}>
              <option value="">Any status</option>
              {Object.entries(LIFECYCLE_STATES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label className={usageStyles.field}>
            <span>Operational</span>
            <select className={styles.filterInput} value={filters.operational_status} onChange={(e) => setFilter('operational_status', e.target.value)}>
              <option value="">Any operational status</option>
              {Object.entries(OPERATIONAL_STATUSES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label className={usageStyles.field}>
            <span>Completion</span>
            <select className={styles.filterInput} value={filters.completion_status} onChange={(e) => setFilter('completion_status', e.target.value)}>
              <option value="">Any completion</option>
              {Object.entries(COMPLETION).map(([value, { label }]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label className={usageStyles.field}>
            <span>Issue</span>
            <select className={styles.filterInput} value={filters.issue} onChange={(e) => setFilter('issue', e.target.value)}>
              <option value="">Any issue</option>
              {Object.entries(ISSUES).map(([value, { label }]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <div className={usageStyles.filterActions}>
            <button type="button" className={styles.secondaryButton} onClick={() => { setFilters(defaultFilters()); setPage(1); }}>
              Reset
            </button>
            <button type="button" className={styles.secondaryButton} onClick={exportCsv} disabled={!sortedItems.length}>
              Export CSV
            </button>
          </div>
        </div>
      </section>

      {error ? <div className={styles.error}>{error}</div> : null}

      {summary ? (
        <>
          <div className={styles.kpiGrid}>
            <KpiCard label="Equipment" value={summary.equipment} hint={`${summary.facilities} facilit${summary.facilities === 1 ? 'y' : 'ies'}`} />
            <KpiCard label="Worklists" value={summary.worklists} hint="Requests published to machines" />
            <KpiCard label="Pulled" value={summary.pulled} hint={`${percent(summary.pulled, summary.worklists)}% of worklists`} />
            <KpiCard label="Completed" value={summary.completed} hint={`${percent(summary.completed, summary.worklists)}% of worklists`} />
            <KpiCard label="Studies received" value={summary.studies} />
          </div>

          <div className={usageStyles.issueChips}>
            {Object.entries(ISSUES).map(([value, { label }]) => (
              <button
                type="button"
                key={value}
                className={`${usageStyles.issueChip} ${filters.issue === value ? usageStyles.issueChipActive : ''}`}
                onClick={() => setFilter('issue', filters.issue === value ? '' : value)}
                aria-pressed={filters.issue === value}
              >
                <strong>{summary.issues[value] || 0}</strong> {label}
              </button>
            ))}
          </div>
        </>
      ) : null}

      <section className={styles.card}>
        <div className={`${styles.tableWrap} ${styles.tableFramed}`}>
          <table className={styles.table}>
            <thead>
              <tr>
                {COLUMNS.map((column) => {
                  const active = sort.key === column.key;
                  return (
                    <th
                      key={column.key}
                      aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                    >
                      <button type="button" className={usageStyles.sortButton} onClick={() => toggleSort(column)}>
                        {column.label}
                        <span className={active ? usageStyles.sortActive : usageStyles.sortIdle} aria-hidden="true">
                          {active && sort.direction === 'desc' ? '▼' : '▲'}
                        </span>
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {pageItems.map((row) => {
                const issue = ISSUES[row.issue] || { label: row.issue, tone: 'cancelled' };
                return (
                  <tr key={row.equipment_id}>
                    <td className={usageStyles.facilityCell}>
                      {row.facility_name}
                      <span className={styles.subText}>{[row.facility_id, row.county, row.hmis].filter(Boolean).join(' · ')}</span>
                    </td>
                    <td>{row.modality || '—'}</td>
                    <td>
                      {linkEquipment
                        ? <Link to={`/equipment/${row.equipment_id}`}>{row.equipment_name}</Link>
                        : row.equipment_name}
                      <span className={styles.subText}>
                        {row.ae_title ? <code>{row.ae_title}</code> : 'no AE title'}
                        {row.is_linked ? '' : ' · not linked'}
                        {row.lifecycle_state && row.lifecycle_state !== 'active' ? ` · ${LIFECYCLE_STATES[row.lifecycle_state] || row.lifecycle_state}` : ''}
                        {row.operational_status && row.operational_status !== 'operational' ? ` · ${OPERATIONAL_STATUSES[row.operational_status] || row.operational_status}` : ''}
                      </span>
                    </td>
                    <td>{row.vendor_name || '—'}</td>
                    <td className={usageStyles.number}>{row.worklists}</td>
                    <td className={usageStyles.number}>
                      {row.pulled}
                      {row.queries ? <span className={styles.subText}>{row.queries} queries</span> : null}
                    </td>
                    <td className={usageStyles.number}>
                      {row.completed}
                      {row.worklists ? <span className={styles.subText}>{percent(row.completed, row.worklists)}%</span> : null}
                    </td>
                    <td className={usageStyles.number}>
                      {row.studies}
                      {row.unmatched_studies ? <span className={styles.subText}>{row.unmatched_studies} unmatched</span> : null}
                    </td>
                    <td className={styles.nowrap}>{formatDateTime(row.last_pulled_at)}</td>
                    <td className={styles.nowrap}>{formatDateTime(row.last_completed_at)}</td>
                    <td className={usageStyles.issueCell}>
                      <StatusPill value={issue.tone} label={issue.label} />
                      {row.issue_detail ? <span className={styles.subText}>{row.issue_detail}</span> : null}
                    </td>
                  </tr>
                );
              })}
              {pageItems.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length} className={styles.muted}>
                    {loading ? 'Loading…' : 'No equipment matches these filters.'}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <Pagination page={page} pageSize={PAGE_SIZE} total={sortedItems.length} loading={loading} onPageChange={setPage} />
      </section>
    </>
  );
};

export default EquipmentUsageView;
